import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Empty,
  Statistic,
  Row,
  Col,
  Card,
  Tag,
  Input,
  Form,
  Modal,
  Alert,
  Divider
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, HomeOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AFTER_SALES_STATUS_TEXT, AfterSalesRequest } from '../../types/afterSales';
import '../MemberManagement.css';

const { Text, Paragraph, Title } = Typography;
const { RangePicker } = DatePicker;

const BILLING_TOKEN_KEY = 'teamBillingToken';

const CATEGORY_OPTIONS = [
  { value: '耗材申请', label: '耗材申请' },
  { value: '试剂申请', label: '试剂申请' },
  { value: '物品报修', label: '物品报修' },
  { value: '测试申请', label: '测试申请' },
  { value: '出差申请', label: '出差申请' },
  { value: '财务报销', label: '财务报销' }
];

type StatusData = {
  passwordConfigured: boolean;
  emailMasked: string;
  hasEmail: boolean;
  locked?: boolean;
  lockedUntil?: string | null;
};

type BillingOrder = {
  _id: string;
  category: string;
  title?: string;
  applicant: string;
  settleAmount: number;
  discountedAmount?: number | null;
  afterSales?: AfterSalesRequest[];
  afterSalesAdjustment?: number;
  billingAdjustedAmount?: number;
  billDate?: string | null;
  approvedAt?: string | null;
  reason?: string;
  consumableProjects?: Array<Record<string, unknown>>;
  reagentProjects?: Array<Record<string, unknown>>;
  testProjects?: Array<Record<string, unknown>>;
  trip?: Record<string, string>;
  financeReimbursement?: { claimAmount?: number; expenseType?: string; remark?: string };
  repair?: { title?: string; reason?: string; priority?: string };
  merchantOrder?: {
    merchantName?: string;
    orderStatus?: string;
    platformOrderNote?: {
      completed?: boolean;
      price?: number | null;
      link?: string;
      submittedAt?: string | null;
    };
  } | null;
};

type ExportLog = {
  _id: string;
  createdAt: string;
  exporterName?: string;
  exporterUsername?: string;
  scope: string;
  categories: string[];
  startDate?: string;
  endDate?: string;
  orderCount: number;
  totalAmount: number;
  ip?: string;
  location?: string;
  device?: string;
};

async function apiFetch(path: string, options?: RequestInit & { billing?: boolean }) {
  const token = localStorage.getItem('token');
  const billingToken = sessionStorage.getItem(BILLING_TOKEN_KEY);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.billing && billingToken ? { 'X-Team-Billing-Token': billingToken } : {}),
    ...((options?.headers as Record<string, string>) || {})
  };
  const res = await fetch(`/api/team-billing${path}`, { ...options, headers });
  const body = await res.json().catch(() => ({ success: false, error: '响应解析失败' }));
  if (body?.code === 'BILLING_LOCKED') {
    sessionStorage.removeItem(BILLING_TOKEN_KEY);
  }
  return { httpStatus: res.status, ...body };
}

function fmtMoney(n?: number | null) {
  return `¥${Number(n || 0).toFixed(2)}`;
}

function fmtDate(v?: string | null) {
  if (!v) return '—';
  return dayjs(v).format('YYYY-MM-DD HH:mm');
}

function writeWorkbook(fileName: string, snapshot: {
  teamName?: string;
  categories: string[];
  startDate?: string;
  endDate?: string;
  summary: {
    orderCount: number;
    totalAmount: number;
    discountedTotalAmount?: number;
    afterSalesAdjustmentAmount?: number;
    billingAdjustedTotalAmount?: number;
  };
  orders: BillingOrder[];
}) {
  const summarySheet = XLSX.utils.aoa_to_sheet([
    ['团队', snapshot.teamName || ''],
    ['导出类型', snapshot.categories.join('、')],
    ['账单区间', `${snapshot.startDate || '—'} ~ ${snapshot.endDate || '—'}`],
    ['记录数', snapshot.summary.orderCount],
    ['金额合计', snapshot.summary.totalAmount],
    ['优惠后合计', snapshot.summary.discountedTotalAmount ?? 0],
    ['售后调整合计', snapshot.summary.afterSalesAdjustmentAmount ?? 0],
    ['售后调整后合计', snapshot.summary.billingAdjustedTotalAmount ?? 0],
    ['导出时间', dayjs().format('YYYY-MM-DD HH:mm:ss')]
  ]);

  const detailRows: Array<Record<string, string | number>> = [];
  for (const order of snapshot.orders) {
    detailRows.push({
      申请类型: order.category,
      审批通过时间: fmtDate(order.billDate || order.approvedAt),
      标题: order.title || '',
      申请人: order.applicant,
      结算金额: Number(order.settleAmount || 0),
      优惠后价格: order.discountedAmount ?? '',
      售后状态: (order.afterSales || []).map((row) => AFTER_SALES_STATUS_TEXT[row.status]).join('、'),
      售后调整金额: order.afterSalesAdjustment ?? 0,
      售后调整后金额: order.billingAdjustedAmount ?? order.discountedAmount ?? order.settleAmount,
      备注价格: order.merchantOrder?.platformOrderNote?.submittedAt
        ? order.merchantOrder.platformOrderNote.price ?? ''
        : '',
      备注链接: order.merchantOrder?.platformOrderNote?.submittedAt
        ? order.merchantOrder.platformOrderNote.link || ''
        : '',
      事由: order.reason || '',
      商家: order.merchantOrder?.merchantName || '',
      明细摘要: summarizeDetail(order)
    });
  }
  const detailSheet = XLSX.utils.json_to_sheet(detailRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, summarySheet, '汇总');
  XLSX.utils.book_append_sheet(wb, detailSheet, '明细');
  XLSX.writeFile(wb, fileName);
}

function summarizeDetail(order: BillingOrder): string {
  if (order.category === '耗材申请') {
    return (order.consumableProjects || [])
      .map((p) => `${p.name || ''}×${p.amount || ''}`)
      .join('；')
      .slice(0, 200);
  }
  if (order.category === '试剂申请') {
    return (order.reagentProjects || [])
      .map((p) => `${p.name || p.cas || ''}×${p.amount || ''}`)
      .join('；')
      .slice(0, 200);
  }
  if (order.category === '测试申请') {
    return (order.testProjects || [])
      .map((p) => `${p.project || ''}×${p.quantity || ''}`)
      .join('；')
      .slice(0, 200);
  }
  if (order.category === '出差申请' && order.trip) {
    return `${order.trip.destination || ''} ${order.trip.tripStartDate || ''}~${order.trip.tripEndDate || ''}`.trim();
  }
  if (order.category === '财务报销' && order.financeReimbursement) {
    return order.financeReimbursement.expenseType || order.financeReimbursement.remark || '';
  }
  if (order.category === '物品报修' && order.repair) {
    return order.repair.reason || order.repair.title || '';
  }
  return '';
}

const TeamBilling: React.FC = () => {
  const { role } = useAuth();
  const [status, setStatus] = useState<StatusData | null>(null);
  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem(BILLING_TOKEN_KEY));
  const [bootLoading, setBootLoading] = useState(true);

  const [setupForm] = Form.useForm();
  const [unlockForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [resetOpen, setResetOpen] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  const [category, setCategory] = useState('耗材申请');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<BillingOrder[]>([]);
  const [summary, setSummary] = useState({
    orderCount: 0,
    totalAmount: 0,
    discountedTotalAmount: 0,
    afterSalesAdjustmentAmount: 0,
    billingAdjustedTotalAmount: 0
  });
  const [exportLogs, setExportLogs] = useState<ExportLog[]>([]);
  const [exporting, setExporting] = useState(false);

  const loadStatus = useCallback(async () => {
    setBootLoading(true);
    try {
      const body = await apiFetch('/status');
      if (!body.success) {
        message.error(body.error || '无法加载账单状态');
        setStatus(null);
        return;
      }
      setStatus(body.data);
    } finally {
      setBootLoading(false);
    }
  }, []);

  const loadBills = useCallback(async () => {
    if (!unlocked) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        category,
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD')
      });
      const body = await apiFetch(`/bills?${qs}`, { billing: true });
      if (body.code === 'BILLING_LOCKED') {
        setUnlocked(false);
        message.warning('账单会话已失效，请重新验证密码');
        return;
      }
      if (!body.success) {
        message.error(body.error || '加载失败');
        setOrders([]);
        return;
      }
      setOrders(body.data.orders || []);
      setSummary(body.data.summary || {
        orderCount: 0,
        totalAmount: 0,
        discountedTotalAmount: 0,
        afterSalesAdjustmentAmount: 0,
        billingAdjustedTotalAmount: 0
      });
    } finally {
      setLoading(false);
    }
  }, [unlocked, category, range]);

  const loadExportLogs = useCallback(async () => {
    if (!unlocked) return;
    const body = await apiFetch('/export-logs', { billing: true });
    if (body.success) setExportLogs(body.data || []);
  }, [unlocked]);

  useEffect(() => {
    if (role === '管理员') void loadStatus();
  }, [role, loadStatus]);

  useEffect(() => {
    if (unlocked) {
      void loadBills();
      void loadExportLogs();
    }
  }, [unlocked, loadBills, loadExportLogs]);

  const onSendSetupCode = async () => {
    setCodeSending(true);
    try {
      const body = await apiFetch('/setup/send-code', { method: 'POST', body: '{}' });
      if (!body.success) {
        message.error(body.error || '发送失败');
        return;
      }
      message.success(body.message || '验证码已发送');
    } finally {
      setCodeSending(false);
    }
  };

  const onSetup = async () => {
    const values = await setupForm.validateFields();
    setAuthSubmitting(true);
    try {
      const body = await apiFetch('/setup', {
        method: 'POST',
        body: JSON.stringify({ code: values.code, password: values.password })
      });
      if (!body.success) {
        message.error(body.error || '设置失败');
        return;
      }
      sessionStorage.setItem(BILLING_TOKEN_KEY, body.data.billingToken);
      setUnlocked(true);
      message.success('访问密码已设置');
      void loadStatus();
    } finally {
      setAuthSubmitting(false);
    }
  };

  const onUnlock = async () => {
    const values = await unlockForm.validateFields();
    setAuthSubmitting(true);
    try {
      const body = await apiFetch('/unlock', {
        method: 'POST',
        body: JSON.stringify({ password: values.password })
      });
      if (!body.success) {
        message.error(body.error || '验证失败');
        void loadStatus();
        return;
      }
      sessionStorage.setItem(BILLING_TOKEN_KEY, body.data.billingToken);
      setUnlocked(true);
      message.success('已解锁团队账单');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const onSendResetCode = async () => {
    setCodeSending(true);
    try {
      const body = await apiFetch('/reset/send-code', { method: 'POST', body: '{}' });
      if (!body.success) {
        message.error(body.error || '发送失败');
        return;
      }
      message.success(body.message || '验证码已发送');
    } finally {
      setCodeSending(false);
    }
  };

  const onReset = async () => {
    const values = await resetForm.validateFields();
    setAuthSubmitting(true);
    try {
      const body = await apiFetch('/reset', {
        method: 'POST',
        body: JSON.stringify({ code: values.code, password: values.password })
      });
      if (!body.success) {
        message.error(body.error || '重置失败');
        return;
      }
      sessionStorage.setItem(BILLING_TOKEN_KEY, body.data.billingToken);
      setUnlocked(true);
      setResetOpen(false);
      resetForm.resetFields();
      message.success('访问密码已重置');
      void loadStatus();
    } finally {
      setAuthSubmitting(false);
    }
  };

  const doExport = async (scope: 'all' | 'single') => {
    setExporting(true);
    try {
      const body = await apiFetch('/export', {
        method: 'POST',
        billing: true,
        body: JSON.stringify({
          scope,
          category: scope === 'single' ? category : undefined,
          startDate: range[0].format('YYYY-MM-DD'),
          endDate: range[1].format('YYYY-MM-DD')
        })
      });
      if (body.code === 'BILLING_LOCKED') {
        setUnlocked(false);
        message.warning('账单会话已失效，请重新验证密码');
        return;
      }
      if (!body.success) {
        message.error(body.error || '导出失败');
        return;
      }
      const cats: string[] = body.data.categories || [];
      const label = scope === 'all' ? '全部类型' : cats[0] || category;
      writeWorkbook(
        `团队账单_${label}_${range[0].format('YYYYMMDD')}-${range[1].format('YYYYMMDD')}.xlsx`,
        body.data
      );
      message.success('已导出，并写入加密审计记录');
      void loadExportLogs();
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<BillingOrder> = useMemo(
    () => [
      {
        title: '通过时间',
        dataIndex: 'billDate',
        width: 160,
        render: (v, row) => fmtDate(v || row.approvedAt)
      },
      { title: '标题', dataIndex: 'title', ellipsis: true, render: (v) => v || '—' },
      { title: '申请人', dataIndex: 'applicant', width: 100 },
      {
        title: '金额',
        dataIndex: 'settleAmount',
        width: 120,
        align: 'right',
        render: (v) => <Text strong>{fmtMoney(v)}</Text>
      },
      {
        title: '优惠后价格',
        dataIndex: 'discountedAmount',
        width: 130,
        align: 'right',
        render: (v) => (v == null ? <Text type="secondary">—</Text> : <Text strong>{fmtMoney(v)}</Text>)
      },
      {
        title: '售后处理',
        width: 180,
        render: (_, row) =>
          row.afterSales?.length ? (
            <Space direction="vertical" size={2}>
              {row.afterSales.map((request) => (
                <Tag key={request._id}>
                  {request.type === 'return' ? '退货' : '换货'} · {AFTER_SALES_STATUS_TEXT[request.status]}
                </Tag>
              ))}
            </Space>
          ) : <Text type="secondary">—</Text>
      },
      {
        title: '售后调整',
        dataIndex: 'afterSalesAdjustment',
        width: 110,
        align: 'right',
        render: (value) => Number(value || 0) === 0 ? '—' : fmtMoney(value)
      },
      {
        title: '调整后金额',
        dataIndex: 'billingAdjustedAmount',
        width: 125,
        align: 'right',
        render: (value) => <Text strong>{fmtMoney(value)}</Text>
      },
      {
        title: '备注价格',
        width: 110,
        align: 'right',
        render: (_, row) =>
          row.merchantOrder?.platformOrderNote?.submittedAt
            ? fmtMoney(row.merchantOrder.platformOrderNote.price)
            : <Text type="secondary">—</Text>
      },
      {
        title: '备注链接',
        width: 220,
        ellipsis: true,
        render: (_, row) =>
          row.merchantOrder?.platformOrderNote?.submittedAt &&
          row.merchantOrder.platformOrderNote.link ? (
            <Button
              type="link"
              size="small"
              onClick={() =>
                Modal.info({
                  title: '平台下单链接',
                  content: (
                    <div style={{ wordBreak: 'break-all' }}>
                      {row.merchantOrder?.platformOrderNote?.link}
                    </div>
                  )
                })
              }
            >
              查看链接
            </Button>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: '摘要',
        ellipsis: true,
        render: (_, row) => summarizeDetail(row) || row.reason || '—'
      }
    ],
    []
  );

  const logColumns: ColumnsType<ExportLog> = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v) => fmtDate(v)
    },
    {
      title: '操作人',
      width: 120,
      render: (_, r) => r.exporterName || r.exporterUsername || '—'
    },
    {
      title: '范围',
      width: 160,
      render: (_, r) =>
        r.scope === 'all' ? <Tag color="blue">全部类型</Tag> : <Tag>{(r.categories || []).join('、')}</Tag>
    },
    {
      title: '区间',
      width: 200,
      render: (_, r) => `${r.startDate || '—'} ~ ${r.endDate || '—'}`
    },
    { title: '笔数', dataIndex: 'orderCount', width: 70 },
    {
      title: '合计',
      dataIndex: 'totalAmount',
      width: 110,
      render: (v) => fmtMoney(v)
    },
    { title: 'IP', dataIndex: 'ip', width: 130, render: (v) => v || '—' },
    { title: '访问地', dataIndex: 'location', width: 140, render: (v) => v || '—' },
    { title: '设备', dataIndex: 'device', width: 200, ellipsis: true, render: (v) => v || '—' }
  ];

  if (role !== '管理员') {
    return <Navigate to="/dashboard" replace />;
  }

  if (bootLoading && !status) {
    return (
      <div className="page-container full-height members-lims">
        <Card loading style={{ marginTop: 24 }} />
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="page-container full-height members-lims">
        <nav className="mm-crumb" aria-label="breadcrumb">
          <HomeOutlined />
          <span>首页</span>
          <span className="mm-crumb-sep">/</span>
          <span>团队账单</span>
        </nav>
        <header className="mm-page-head">
          <h1 className="mm-title">团队账单</h1>
          <p className="mm-subtitle mb-0">敏感财务数据，需管理员访问密码与邮箱验证保护。</p>
        </header>

        <div className="content-area px-1 px-md-2" style={{ maxWidth: 520 }}>
          <Card>
            <Space align="start" style={{ marginBottom: 16 }}>
              <LockOutlined style={{ fontSize: 28, color: 'var(--lims-primary, #00355f)' }} />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {status?.passwordConfigured ? '验证访问密码' : '首次设置访问密码'}
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  验证邮箱：{status?.emailMasked || '—'}
                </Paragraph>
              </div>
            </Space>

            {!status?.hasEmail && (
              <Alert
                type="error"
                showIcon
                className="mb-3"
                message="当前管理员账号未绑定邮箱，请先在个人资料中绑定邮箱后再设置账单密码。"
              />
            )}

            {status?.locked && (
              <Alert
                type="warning"
                showIcon
                className="mb-3"
                message="尝试次数过多，账户已临时锁定，请稍后再试或通过邮箱重置密码。"
              />
            )}

            {!status?.passwordConfigured ? (
              <Form form={setupForm} layout="vertical" onFinish={() => void onSetup()}>
                <Form.Item>
                  <Button
                    icon={<SafetyCertificateOutlined />}
                    loading={codeSending}
                    disabled={!status?.hasEmail}
                    onClick={() => void onSendSetupCode()}
                  >
                    发送邮箱验证码
                  </Button>
                </Form.Item>
                <Form.Item
                  name="code"
                  label="邮箱验证码"
                  rules={[{ required: true, message: '请输入验证码' }]}
                >
                  <Input placeholder="6 位验证码" maxLength={6} />
                </Form.Item>
                <Form.Item
                  name="password"
                  label="设置访问密码"
                  rules={[
                    { required: true, message: '请设置密码' },
                    { min: 8, message: '至少 8 位' }
                  ]}
                >
                  <Input.Password placeholder="至少 8 位，请妥善保管" />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  label="确认密码"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: '请再次输入' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) return Promise.resolve();
                        return Promise.reject(new Error('两次密码不一致'));
                      }
                    })
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={authSubmitting} block>
                  确认设置并进入
                </Button>
              </Form>
            ) : (
              <Form form={unlockForm} layout="vertical" onFinish={() => void onUnlock()}>
                <Form.Item
                  name="password"
                  label="访问密码"
                  rules={[{ required: true, message: '请输入访问密码' }]}
                >
                  <Input.Password placeholder="团队账单访问密码" autoFocus />
                </Form.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button type="primary" htmlType="submit" loading={authSubmitting} block>
                    解锁账单
                  </Button>
                  <Button type="link" onClick={() => setResetOpen(true)}>
                    忘记密码？通过邮箱验证码重置
                  </Button>
                </Space>
              </Form>
            )}
          </Card>
        </div>

        <Modal
          title="重置团队账单访问密码"
          open={resetOpen}
          onCancel={() => setResetOpen(false)}
          onOk={() => void onReset()}
          confirmLoading={authSubmitting}
          okText="确认重置"
        >
          <Form form={resetForm} layout="vertical">
            <Form.Item>
              <Button loading={codeSending} onClick={() => void onSendResetCode()}>
                发送验证码至 {status?.emailMasked}
              </Button>
            </Form.Item>
            <Form.Item name="code" label="验证码" rules={[{ required: true, message: '必填' }]}>
              <Input maxLength={6} />
            </Form.Item>
            <Form.Item
              name="password"
              label="新访问密码"
              rules={[
                { required: true, message: '必填' },
                { min: 8, message: '至少 8 位' }
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>团队账单</span>
      </nav>

      <header className="mm-page-head d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="mm-title">团队账单</h1>
          <p className="mm-subtitle mb-0">
            统计已通过的财务相关申请；导出将加密存档并记录操作日志。
          </p>
        </div>
        <Space wrap>
          <Select
            value={category}
            onChange={setCategory}
            style={{ minWidth: 160 }}
            options={CATEGORY_OPTIONS}
          />
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(vals) => {
              if (vals?.[0] && vals?.[1]) setRange([vals[0], vals[1]]);
            }}
          />
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={() => void doExport('single')}
          >
            导出当前类型
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={() => void doExport('all')}
          >
            导出全部类型
          </Button>
          <Button
            onClick={() => {
              sessionStorage.removeItem(BILLING_TOKEN_KEY);
              setUnlocked(false);
            }}
          >
            锁定
          </Button>
        </Space>
      </header>

      <div className="content-area px-1 px-md-2">
        <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3 mb-3">
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic title={`${category} · 笔数`} value={summary.orderCount} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic title="金额合计" value={summary.totalAmount} precision={2} prefix="¥" />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="优惠后合计"
                  value={summary.discountedTotalAmount}
                  precision={2}
                  prefix="¥"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="售后调整合计"
                  value={summary.afterSalesAdjustmentAmount}
                  precision={2}
                  prefix="¥"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="售后调整后合计"
                  value={summary.billingAdjustedTotalAmount}
                  precision={2}
                  prefix="¥"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="区间"
                  value={`${range[0].format('YYYY-MM-DD')} ~ ${range[1].format('YYYY-MM-DD')}`}
                  valueStyle={{ fontSize: 15 }}
                />
              </Card>
            </Col>
          </Row>

          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={orders}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="该类型在所选日期内暂无已通过申请" /> }}
            scroll={{ x: 800 }}
          />
        </div>

        <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3">
          <Divider orientation="left" style={{ marginTop: 8 }}>
            导出记录（快照已 AES 加密存档）
          </Divider>
          <Table
            rowKey="_id"
            size="small"
            columns={logColumns}
            dataSource={exportLogs}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '暂无导出记录' }}
            scroll={{ x: 1200 }}
          />
        </div>
      </div>
    </div>
  );
};

export default TeamBilling;
